import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Blob from '@/components/Blob'
import Illustration from '@/components/Illustration'
import Card from '@/components/ui/Card'
import AdminGrantCredits from '@/components/admin/AdminGrantCredits'
import AdminPortalAccess from '@/components/admin/AdminPortalAccess'
import { useAuth } from '@/lib/auth-context'

interface ProgramItem {
  id?: string;
  program?: string;
  owner_name?: string;
  status?: string;
  submitted_at?: string;
}

interface DonationsSummary {
  month_to_date_cents?: number;
  year_to_date_cents?: number;
  previous_month_cents?: number;
  delta_vs_prior_month?: number;
}

interface UsersByRole {
  sponsors?: number;
  teachers?: number;
  admins?: number;
  pending_invites?: number;
}

interface SectionCardProps {
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
}

const navItems = [
  { label: 'Overview', href: '#overview', current: true },
  { label: 'Programs', href: '#programs' },
  { label: 'Donations', href: '#donations' },
  { label: 'Users', href: '#users' },
  { label: 'Settings', href: '#settings' },
]

const formatStatus = (value: string | null | undefined): string => {
  if (!value) return 'Pending'
  return String(value)
    .split('_')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
    .join(' ')
}

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return '—'
  }
}

const formatCurrency = (cents: number | null | undefined): string => {
  if (typeof cents !== 'number' || Number.isNaN(cents)) return '$0.00'
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

const formatDelta = (delta: number | null | undefined, prev: number): string => {
  if (typeof delta !== 'number' || Number.isNaN(delta)) {
    return prev > 0 ? '— vs. prior month' : 'No prior month data yet'
  }
  const pct = delta * 100
  const prefix = pct > 0 ? '+' : ''
  return `${prefix}${pct.toFixed(1)}% vs. prior month`
}

const SectionCard = ({ id, title, description, children }: SectionCardProps) => (
  <Card id={id} className="text-cocoa space-y-4 md:p-8">
    <header className="space-y-1">
      <h2 className="text-xl font-semibold">{title}</h2>
      {description ? (
        <p className="text-sm text-cocoa/70">{description}</p>
      ) : null}
    </header>
    {children}
  </Card>
)

const TOOL_SECTIONS = [
  {
    title: 'People',
    items: [
      { label: 'Users Directory', to: '/admin/users' },
      { label: 'Classrooms Directory', to: '/admin/classrooms' },
      { label: 'Create User', to: '/admin/create-user' },
      { label: 'Provision Teacher & Classroom', to: '/admin/provision-teacher' },
      { label: 'Sponsors', to: '/admin/sponsors' },
      { label: 'Nominations', to: '/admin/nominations' },
      { label: 'Allowlist Import', to: '/admin/allowlist/import' },
    ],
  },
  {
    title: 'Programs',
    items: [
      { label: 'Budgets', to: '/admin/budgets' },
      { label: 'Payments & Credits', to: '/admin/orders' },
      { label: 'Randomizer', to: '/admin/randomizer' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Seasonal Decor', to: '/admin/seasonal' },
      { label: 'Marketplace (Admin)', to: '/portal/admin/marketplace' },
      { label: 'Reimbursements', to: '/admin/reimbursements' },
      { label: 'Support Dashboard', to: '/admin/support' },
      { label: 'Support Tickets', to: '/admin/tickets' },
      { label: 'Support Ops Dashboard', to: '/admin/ops-dashboard' },
    ],
  },
  {
    title: 'Insights',
    items: [
      { label: 'Broadcasts', to: '/portal/admin/broadcasts' },
      { label: 'Analytics', to: '/admin/analytics' },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Audit Log', to: '/admin/audit' },
      { label: 'Role History', to: '/admin/role-history' },
      { label: 'Site Settings', to: '/admin/settings' },
    ],
  },
]

export default function AdminDashboard() {
  const { session, isSuperAdmin } = useAuth()
  const token = session?.access_token
  const [unreadCount, setUnreadCount] = useState(0)
  const lastCountRef = useRef(0)
  const [toastMessage, setToastMessage] = useState('')
  const [programs, setPrograms] = useState<ProgramItem[]>([])
  const [programsLoading, setProgramsLoading] = useState(false)
  const [programsError, setProgramsError] = useState('')
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState('')
  const [donations, setDonations] = useState<DonationsSummary | null>(null)
  const [usersByRole, setUsersByRole] = useState<UsersByRole | null>(null)

  const loadPrograms = useCallback(async () => {
    if (!token) return
    setProgramsLoading(true)
    setProgramsError('')
    try {
      const response = await fetch('/api/v1/admin/programs/pending?limit=5', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(json?.error || 'Failed to load programs')
      }
      const programItems = json?.data?.items ?? json?.items
      setPrograms(Array.isArray(programItems) ? programItems : [])
    } catch (error: unknown) {
      console.warn('[admin-dashboard] programs fetch failed', error)
      setPrograms([])
      setProgramsError(error instanceof Error ? error.message : 'Unable to load programs')
    } finally {
      setProgramsLoading(false)
    }
  }, [token])

  const loadSummary = useCallback(async () => {
    if (!token) return
    setSummaryLoading(true)
    setSummaryError('')
    try {
      const response = await fetch('/api/v1/admin/analytics/summary', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(json?.error?.message || json?.error || 'Failed to load analytics summary')
      }
      const d = json?.data || json
      setDonations(d?.donations || null)
      setUsersByRole(d?.users_by_role || d || null)
    } catch (error: unknown) {
      console.warn('[admin-dashboard] analytics fetch failed', error)
      setDonations(null)
      setUsersByRole(null)
      setSummaryError(error instanceof Error ? error.message : 'Unable to load analytics summary')
    } finally {
      setSummaryLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (!token) return
    let cancelled = false

    const load = async () => {
      try {
        const res = await fetch(
          '/api/v1/admin/notifications?unread=true',
          { headers: { Authorization: `Bearer ${token}` } }
        )
        const payload = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(payload?.error?.message || payload?.error || 'Failed to load notifications')
        const items = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.items) ? payload.items : []
        const total = Number(payload?.data?.length ?? items.length)
        if (!cancelled) {
          setUnreadCount(total)
          if (total > lastCountRef.current) {
            setToastMessage(`You have ${total} unread notification${total === 1 ? '' : 's'}.`)
          }
          lastCountRef.current = total
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[admin-dashboard] notification fetch failed', err)
          setUnreadCount(0)
        }
      }
    }

    load()
    const interval = setInterval(load, 60000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [token])

  useEffect(() => {
    if (!token) return undefined

    loadPrograms()
    loadSummary()

    const interval = setInterval(() => {
      loadPrograms()
      loadSummary()
    }, 60000)

    return () => {
      clearInterval(interval)
    }
  }, [token, loadPrograms, loadSummary])

  useEffect(() => {
    if (!toastMessage) return undefined
    const timeout = setTimeout(() => setToastMessage(''), 5000)
    return () => clearTimeout(timeout)
  }, [toastMessage])

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => isSuperAdmin || item.label !== 'Settings'),
    [isSuperAdmin],
  )

  return (
    <div className="min-h-screen bg-sand py-10">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {toastMessage ? (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm">
            {toastMessage}
          </div>
        ) : null}
        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          <aside className="lg:sticky lg:top-24">
            <nav className="rounded-2xl bg-shell shadow-soft p-4">
              <ul className="space-y-2">
                {visibleNavItems.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className={`block rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        item.current
                          ? 'bg-brick text-white shadow'
                          : 'text-cocoa hover:bg-white/70'
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="space-y-8">
            <header className="space-y-4" id="overview">
              <div className="space-y-2">
                <p className="text-sm uppercase tracking-[0.2em] text-cocoa/60">
                  Admin Portal
                </p>
                <h1 className="text-3xl md:text-4xl font-semibold text-cocoa">
                  Organization Overview
                </h1>
                <p className="text-sm text-cocoa/70 max-w-2xl">
                  Keep a pulse on every program, donation, and community member. These
                  starter panels provide a springboard for deeper operational tools.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link className="btn btn-primary inline-flex items-center" to="/admin/provision-teacher">
                  + Provision Teacher &amp; Classroom
                </Link>
                <Link
                  className="inline-flex items-center rounded-full border border-brick px-4 py-2 text-sm font-semibold text-brick hover:bg-brick/5"
                  to="/admin/notifications"
                >
                  Notifications
                  {unreadCount > 0 ? (
                    <span className="ml-2 inline-flex min-w-[1.5rem] justify-center rounded-full bg-brick px-2 py-0.5 text-xs font-bold text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  ) : null}
                </Link>
              </div>
            </header>

            <section aria-label="Admin tools" className="rounded-2xl border border-white/60 bg-white/70 p-4 space-y-6">
              <h2 className="sr-only">Administrative tools</h2>
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {TOOL_SECTIONS.map((section) => (
                  <div key={section.title} className="space-y-3">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa/70">
                      {section.title}
                    </h3>
                    <ul className="space-y-2">
                      {section.items.map((item) => (
                        <li key={item.to}>
                          <Link
                            className="block rounded-xl border border-white/70 bg-white px-4 py-3 text-sm font-semibold text-cocoa hover:bg-shell/60"
                            to={item.to}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <Card className="relative overflow-hidden bg-gradient-to-tr from-shell to-white" id="overview-card">
              <Blob className="absolute -top-16 -right-10 h-48 w-48 opacity-60" />
              <div className="flex flex-col gap-6 md:flex-row md:items-center">
                <div className="flex-1 space-y-2">
                  <p className="text-sm uppercase tracking-[0.2em] text-cocoa/60">
                    Snapshot
                  </p>
                  <h2 className="text-2xl font-semibold text-cocoa">
                    Build momentum with real-time insight
                  </h2>
                  <p className="text-sm text-cocoa/70">
                    Track submissions, celebrate generosity, and support every classroom from one
                    mission control.
                  </p>
                </div>
                <Illustration
                  name="books"
                  animate
                  className="md:w-36 md:h-36 w-28 h-28"
                />
              </div>
            </Card>

            <div className="grid gap-6">
              <SectionCard
                id="programs"
                title="Programs"
                description="Review submissions from teachers and approve drafts before they go live."
              >
                <div className="overflow-hidden rounded-xl border border-white/40">
                  <table className="min-w-full divide-y divide-white/40">
                    <thead className="bg-white/60">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-cocoa/60">
                        <th className="px-4 py-3">Program</th>
                        <th className="px-4 py-3">Owner</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Submitted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/40 text-sm text-cocoa/80">
                      {programsLoading ? (
                        <tr>
                          <td className="px-4 py-4 text-sm text-cocoa/70" colSpan={4}>
                            Loading pending programs…
                          </td>
                        </tr>
                      ) : programsError ? (
                        <tr>
                          <td className="px-4 py-4 text-sm text-brick" colSpan={4}>
                            {programsError}
                          </td>
                        </tr>
                      ) : programs.length ? (
                        programs.map((item, index) => (
                          <tr key={item.id || index} className={index % 2 === 1 ? 'bg-white/40' : undefined}>
                            <td className="px-4 py-3">{item.program || 'Classroom submission'}</td>
                            <td className="px-4 py-3">{item.owner_name || '—'}</td>
                            <td className="px-4 py-3">{formatStatus(item.status)}</td>
                            <td className="px-4 py-3">{formatDateTime(item.submitted_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-4 py-4 text-sm text-cocoa/60" colSpan={4}>
                            No pending submissions right now.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              <SectionCard
                id="donations"
                title="Donations"
                description="Track monthly momentum and year-to-date totals across all programs."
              >
                {summaryError ? (
                  <p className="mb-3 text-sm text-brick">{summaryError}</p>
                ) : null}
                <dl className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl bg-white/50 p-4">
                    <dt className="text-xs uppercase tracking-wide text-cocoa/60">Month to Date</dt>
                    <dd className="mt-2 text-2xl font-semibold text-cocoa">
                      {summaryLoading && !donations ? 'Loading…' : formatCurrency(donations?.month_to_date_cents ?? 0)}
                    </dd>
                    <p className="mt-1 text-xs text-cocoa/60">
                      {summaryLoading && donations ? 'Refreshing…' : formatDelta(donations?.delta_vs_prior_month, donations?.previous_month_cents ?? 0)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-white/50 p-4">
                    <dt className="text-xs uppercase tracking-wide text-cocoa/60">Year to Date</dt>
                    <dd className="mt-2 text-2xl font-semibold text-cocoa">
                      {summaryLoading && !donations ? 'Loading…' : formatCurrency(donations?.year_to_date_cents ?? 0)}
                    </dd>
                    <p className="mt-1 text-xs text-cocoa/60">
                      {donations?.previous_month_cents
                        ? `Prior month: ${formatCurrency(donations.previous_month_cents)}`
                        : 'No prior month data yet'}
                    </p>
                  </div>
                </dl>
              </SectionCard>

              <SectionCard
                id="users"
                title="Users"
                description="See how your community is growing by role and engagement."
              >
                {summaryError ? (
                  <p className="mb-3 text-sm text-brick">We’ll retry user totals shortly.</p>
                ) : null}
                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="rounded-xl bg-white/50 p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Sponsors</p>
                    <p className="mt-2 text-3xl font-semibold text-cocoa">{usersByRole?.sponsors ?? 0}</p>
                    <p className="text-xs text-cocoa/60">Active sponsors</p>
                  </div>
                  <div className="rounded-xl bg-white/50 p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Teachers</p>
                    <p className="mt-2 text-3xl font-semibold text-cocoa">{usersByRole?.teachers ?? 0}</p>
                    <p className="text-xs text-cocoa/60">Confirmed classrooms</p>
                  </div>
                  <div className="rounded-xl bg-white/50 p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Admins</p>
                    <p className="mt-2 text-3xl font-semibold text-cocoa">{usersByRole?.admins ?? 0}</p>
                    <p className="text-xs text-cocoa/60">Platform operators</p>
                  </div>
                  <div className="rounded-xl bg-white/50 p-4">
                    <p className="text-xs uppercase tracking-wide text-cocoa/60">Pending Invites</p>
                    <p className="mt-2 text-3xl font-semibold text-cocoa">{usersByRole?.pending_invites ?? 0}</p>
                    <p className="text-xs text-cocoa/60">Awaiting acceptance</p>
                  </div>
                </div>
              </SectionCard>

              {isSuperAdmin ? (
                <SectionCard
                  id="settings"
                  title="Settings"
                  description="Configure who can access each portal and manage credit tools. More integrations and automation rules will be added here over time."
                >
                  <div className="space-y-6">
                    <AdminPortalAccess />

                    <div className="border-t border-shell/50 pt-4">
                      <h3 className="mb-2 text-sm font-semibold text-cocoa">Grant Credits (Admin)</h3>
                      <AdminGrantCredits />
                    </div>
                  </div>
                </SectionCard>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
